import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../common/block-text/block-text.component';
import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineEditV2Directive } from '../../base-template-editor-v2/inline-edit/inline-edit-v2.directive';
import { InlineMediaV2Directive } from '../../base-template-editor-v2/inline-edit/inline-media-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import {
  ReservationInitModel,
  ReservationInitVariables,
  ReservationModel,
  ReservationVariables,
} from '../../../../models/email-template/reservation.interface';

/**
 * Mockup-styled Reservation + Contact Details block - a drop-in replacement
 * for app-reservation in block-renderer-v2 (same [block]/[isPreview] inputs
 * and outputs), so it covers both the center-canvas summary
 * (isPreview=true) and the right-panel edit form (isPreview=false). Reuses
 * the real, unmodified app-block-text (image upload/segment/validation),
 * app-field-editor-v2 and app-pin sub-components and the exact same
 * ReservationVariables field names - only the surrounding layout is new. The
 * "Your accommodation" heading is a fixed section label per the mockup (its
 * own crBlockContent hardcodes that exact string) - there's no matching field
 * in ReservationVariables, so it isn't editable here either.
 */
@Component({
  selector: 'app-reservation-v2',
  imports: [CommonModule, ReactiveFormsModule, BlockTextComponent, FieldEditorV2Component, InlineEditV2Directive, InlineMediaV2Directive],
  templateUrl: './reservation-v2.component.html',
  styleUrl: './reservation-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ReservationComponentV2 extends BaseComponent implements OnInit {
  @Input() block: ReservationModel = new ReservationInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new ReservationInitVariables();
    }
  }

  image1Placeholder = 'First Image';
  image2Placeholder = 'Second Image';
  image1HrefPlaceholder = 'Enter Image Redirect Link';
  image2HrefPlaceholder = 'Enter Image Redirect Link';
  image1AltPlaceholder = 'Enter First Image Alt Text';
  image2AltPlaceholder = 'Enter Second Image Alt Text';
  namePlaceholder = 'Enter Property Name';
  addressPlaceholder = 'Enter Address';
  telephonePlaceholder = 'Enter Telephone Number';
  checkInPlaceholder = 'Enter Check-In Time';
  checkOutPlaceholder = 'Enter Check-Out Time';

  protected image1AsUpload = true;
  protected image2AsUpload = true;

  protected toggleImage1Mode(): void {
    this.image1AsUpload = !this.image1AsUpload;
  }

  protected toggleImage2Mode(): void {
    this.image2AsUpload = !this.image2AsUpload;
  }

  private hrefFor(link?: string, segment?: string): string {
    const base = this.helper.getFullUrl(link);
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  get image1Href(): string {
    return this.hrefFor(this.block.variables?.['Res1ImageHref'], this.block.variables?.['Res1ImageHrefSegmentRegex']);
  }

  get image2Href(): string {
    return this.hrefFor(this.block.variables?.['Res2ImageHref'], this.block.variables?.['Res2ImageHrefSegmentRegex']);
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof ReservationVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
